import React from 'react';
import {
  ApplicationIcon,
  Button,
  Chip,
  Icon,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import SliderComponent from '../../../../../shared/components/SliderComponent';
import { useInvertedLocalize } from '../../../../../shared/hooks';

import LICENSE_DETAILS_PANEL_CONSTANTS from './constants';
import type { LicenseDetailsPanelProps } from './type';

import './LicenseDetailsPanel.css';

/**
 * License Details Panel component for displaying comprehensive license information
 * @description Renders a side panel that displays detailed information about a selected license,
 * including financial metrics (total spend, cost per employee), utilization data, optimization score,
 * risk alerts, SKU/renewal details, user roles, and optimization findings.
 * @param {LicenseDetailsPanelProps} props - Component props
 * @param {boolean} props.isOpen - Controls whether the side panel is visible
 * @param {() => void} props.onClose - Callback fired when the panel should close
 * @param {LicenseData | null} props.licenseData - License data object containing all display information; if null, component returns null
 * @returns {React.ReactElement | null} Side panel component or null if licenseData is not provided
 */
function LicenseDetailsPanel({
  isOpen,
  onClose,
  licenseData,
}: LicenseDetailsPanelProps): React.ReactElement | null {
  const localize = useInvertedLocalize();

  if (!licenseData) {
    return null;
  }

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      header={{
        title: `${licenseData.vendor} License Snapshot`,
        headerIcon: <ApplicationIcon logo={licenseData.logo} size='medium' />,
      }}
      layout={
        <div className='licenseDetailsPanel'>
          {/* Status Chip - positioned in header area via CSS */}
          <div className='licenseDetailsPanelStatusChip'>
            <Chip
              label={licenseData.status}
              kind='subtleFilled'
              size='small'
              color='info'
            />
          </div>

          {/* Body */}
          <div className='licenseDetailsPanelBody'>
            {/* Stats Section */}
            <div className='licenseDetailsPanelStats'>
              <div className='licenseDetailsPanelStatItem'>
                <div className='licenseDetailsPanelStatValue'>
                  <Typography
                    kind='body2-bold'
                    className='licenseDetailsPanelStatMainValue'>
                    {licenseData.totalSpend}
                  </Typography>
                  <Typography
                    kind='body3'
                    className={`licenseDetailsPanelStatTrend ${licenseData.spendTrendDirection}`}>
                    {licenseData.spendTrend}
                  </Typography>
                  <div className='licenseDetailsPanelTrendBadge'>
                    <Icon
                      kind={
                        licenseData.spendTrendDirection === 'up'
                          ? 'TrendUp'
                          : 'TrendDown'
                      }
                      size='small'
                    />
                  </div>
                </div>
                <Typography
                  kind='body3'
                  className='licenseDetailsPanelStatLabel'>
                  {localize('Total License Spend')}
                </Typography>
              </div>

              <div className='licenseDetailsPanelStatDivider' />

              <div className='licenseDetailsPanelStatItem'>
                <div className='licenseDetailsPanelStatValue'>
                  <Typography
                    kind='body2-bold'
                    className='licenseDetailsPanelStatMainValue'>
                    {licenseData.utilization}
                  </Typography>
                  <Typography
                    kind='body3'
                    className={`licenseDetailsPanelStatTrend ${licenseData.utilizationTrendDirection}`}>
                    {licenseData.utilizationTrend}
                  </Typography>
                  <div className='licenseDetailsPanelTrendBadge'>
                    <Icon
                      kind={
                        licenseData.utilizationTrendDirection === 'up'
                          ? 'TrendUp'
                          : 'TrendDown'
                      }
                      size='small'
                    />
                  </div>
                </div>
                <Typography
                  kind='body3'
                  className='licenseDetailsPanelStatLabel'>
                  {localize('Average Utilization')}
                </Typography>
              </div>

              <div className='licenseDetailsPanelStatDivider' />

              <div className='licenseDetailsPanelStatItem'>
                <div className='licenseDetailsPanelStatValue'>
                  <Typography
                    kind='body2-bold'
                    className='licenseDetailsPanelStatMainValue'>
                    {licenseData.costPerEmployee}
                  </Typography>
                  <Typography
                    kind='body3'
                    className={`licenseDetailsPanelStatTrend ${licenseData.costTrendDirection}`}>
                    {licenseData.costTrend}
                  </Typography>
                  <div className='licenseDetailsPanelTrendBadge'>
                    <Icon
                      kind={
                        licenseData.costTrendDirection === 'up'
                          ? 'TrendUp'
                          : 'TrendDown'
                      }
                      size='small'
                    />
                  </div>
                </div>
                <Typography
                  kind='body3'
                  className='licenseDetailsPanelStatLabel'>
                  {localize('Cost per Employee')}
                </Typography>
              </div>

              <div className='licenseDetailsPanelStatDivider' />

              <div className='licenseDetailsPanelStatItem'>
                <div className='licenseDetailsPanelStatValue'>
                  <Typography
                    kind='body2-bold'
                    className='licenseDetailsPanelStatMainValue'>
                    {licenseData.optimizationScore}
                  </Typography>
                  <Typography
                    kind='body3'
                    className={`licenseDetailsPanelStatTrend ${licenseData.optimizationTrendDirection}`}>
                    {licenseData.optimizationTrend}
                  </Typography>
                  <div className='licenseDetailsPanelTrendBadge'>
                    <Icon
                      kind={
                        licenseData.optimizationTrendDirection === 'up'
                          ? 'TrendUp'
                          : 'TrendDown'
                      }
                      size='small'
                    />
                  </div>
                </div>
                <Typography
                  kind='body3'
                  className='licenseDetailsPanelStatLabel'>
                  {localize('Optimization Score')}
                </Typography>
              </div>

              <div className='licenseDetailsPanelStatDivider' />

              <div className='licenseDetailsPanelStatItem'>
                <div className='licenseDetailsPanelStatValue'>
                  <Typography
                    kind='body2-bold'
                    className='licenseDetailsPanelStatMainValue'>
                    {licenseData.riskAlerts}
                  </Typography>
                  <Typography
                    kind='body3'
                    className='licenseDetailsPanelRiskLevel'>
                    {licenseData.riskLevel}
                  </Typography>
                </div>
                <Typography
                  kind='body3'
                  className='licenseDetailsPanelStatLabel'>
                  {localize('Risk Alerts')}
                </Typography>
              </div>
            </div>

            {/* SKU Section */}
            <div className='licenseDetailsPanelSKU'>
              <div className='licenseDetailsPanelSKUHeader'>
                <Typography kind='body3' className='sku-header'>
                  {localize('SKU/RENEWAL DATE')}
                </Typography>
                <Typography kind='body3' className='sku-header'>
                  {localize('USER ROLES')}
                </Typography>
                <Typography kind='body3' className='sku-header'>
                  {localize('LICENSE COST / USAGE')}
                </Typography>
              </div>

              <div className='licenseDetailsPanelSKUItem'>
                <div className='licenseDetailsPanelSKULeft'>
                  <div className='licenseDetailsPanelSKUName'>
                    <ApplicationIcon logo={licenseData.logo} size='small' />
                    <Typography kind='body2'>{licenseData.vendor}</Typography>
                  </div>
                  <Typography
                    kind='body3'
                    className='licenseDetailsPanelRenewalDate'>
                    {licenseData.renewalDate}
                  </Typography>
                </div>

                <div className='licenseDetailsPanelSKUMiddle'>
                  {licenseData.userRoles.map((role) => (
                    <Typography
                      key={role}
                      kind='body3'
                      className='licenseDetailsPanelUserRole'>
                      {role}
                    </Typography>
                  ))}
                </div>

                <div className='licenseDetailsPanelSKURight'>
                  <Typography
                    kind='body3'
                    className='licenseDetailsPanelLicenseCost'>
                    {licenseData.licenseCost}
                  </Typography>
                  <div className='licenseDetailsPanelSKUUsage'>
                    <Typography
                      kind='body3'
                      className='licenseDetailsPanelUsageLabel'>
                      {localize('Usage:')} {licenseData.usage}
                    </Typography>
                    <div className='licenseDetailsPanelUsageProgress'>
                      <SliderComponent
                        value={licenseData.usagePercentage}
                        readOnly
                        progressColor='var(--color-blue-500, #0294f7)'
                        trackHeight='0.25rem'
                      />
                      <Typography
                        kind='body3'
                        className='licenseDetailsPanelUsagePercentage'>
                        {licenseData.usagePercentage}
                        {LICENSE_DETAILS_PANEL_CONSTANTS.PERCENT}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Findings Section */}
            {licenseData.findings && (
              <div className='licenseDetailsPanelFindings'>
                <Typography
                  kind='body3'
                  className='licenseDetailsPanelFindingsLabel'>
                  {localize('Findings')}
                </Typography>
                <div className='licenseDetailsPanelFindingsBox'>
                  <Icon
                    kind='SaviCompanion'
                    size='small'
                    className='licenseDetailsPanelFindingsIcon'
                  />
                  <Typography
                    kind='body2'
                    className='licenseDetailsPanelFindingsText'>
                    {licenseData.findings}
                  </Typography>
                  <Icon
                    kind='ArrowRight'
                    size='small'
                    className='licenseDetailsPanelFindingsArrow'
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className='licenseDetailsPanelFooter'>
            <Button
              kind='ghost'
              onClick={onClose}
              className='licenseDetailsPanelCloseBtn'>
              {localize('Close')}
            </Button>
            <Button kind='outlined' className='licenseDetailsPanelDetailsBtn'>
              {localize('View All Details')}
            </Button>
          </div>
        </div>
      }
      className='licenseDetailsPanelSidePanel'
      classNames={{
        headerClassName: 'licenseDetailsPanelHeader',
      }}
    />
  );
}

export default LicenseDetailsPanel;
